import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { type TableComponents, TableVirtuoso } from 'react-virtuoso'
import { TableScroller } from '@/components/Table/TableScroller'
import { TableBody, TableHead, Table } from '@/components/Table'
import type { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { VirtualStripedTableRow } from '@/components/VirtualStripedTableRow'
import {
  VirtualTableInfiniteScrollLoaderContainer,
  type OnPressItemRow,
  type VirtuosoContext,
} from '@/components/VirtualTableInfiniteScrollLoaderContainer'

interface VirtualTableContainerProps<S, T> {
  data: readonly T[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- InfiniteScrollProvider is invariant in its filter type, which the table never reads
  scroller: InfiniteScrollProvider<S, any>
  overscan?: number
  itemContent: (index: number, item: T) => ReactNode
  headerContent: () => ReactNode
  onPressItemRow?: OnPressItemRow<S>
}

/**
 * A virtualized table that loads more rows from the scroller as it scrolls.
 */
export const VirtualTableContainer = observer(function VirtualTableContainer<
  S,
  T,
>({
  data,
  scroller,
  overscan,
  itemContent,
  headerContent,
  onPressItemRow,
}: VirtualTableContainerProps<S, T>) {
  return (
    <TableVirtuoso<T, VirtuosoContext<S>>
      // Rows come from this snapshot, not a live index lookup: Virtuoso can
      // render with a stale count after the source array has been emptied.
      // The copy also gives Virtuoso a new reference when MobX mutates the
      // array in place.
      data={data.slice()}
      endReached={() => scroller.more()}
      overscan={overscan}
      context={{
        scroller,
        onPressItemRow,
      }}
      components={{
        Table,
        TableHead,
        TableBody,
        TableRow: VirtualStripedTableRow,
        Scroller: TableScroller,
        // Not used as the actual placeholder; this is used to show an
        // infinite scroll zone. The `endReached` isn't sufficient.
        // `observer` drops the loader's generic context type.
        TableFoot: VirtualTableInfiniteScrollLoaderContainer as TableComponents<
          T,
          VirtuosoContext<S>
        >['TableFoot'],
      }}
      itemContent={itemContent}
      fixedHeaderContent={headerContent}
      fixedFooterContent={() => true}
    />
  )
})
